import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import api from "@/lib/api";

/**
 * HomeTurnkeyFeatured — Homepage-embedded showcase of the Turnkey Brands
 * marketplace. Fetches from /api/turnkey-brands, shows up to 3 featured
 * brands, and links out to /turnkey for the full grid.
 *
 * Gracefully renders nothing if the API returns zero brands so the home
 * page never shows an empty rail during initial seeding.
 */
const HomeTurnkeyFeatured = () => {
  const [brands, setBrands] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api
      .get("/turnkey-brands")
      .then((r) => {
        // Featured-first, then existing display_order — gives founder marketing
        // control from the Command Center without any code changes.
        const all = r.data.brands || [];
        const sorted = [...all].sort((a, b) => {
          const fa = a.is_featured ? 0 : 1;
          const fb = b.is_featured ? 0 : 1;
          if (fa !== fb) return fa - fb;
          return (a.display_order ?? 100) - (b.display_order ?? 100);
        });
        setBrands(sorted.slice(0, 3));
      })
      .catch(() => setBrands([]))
      .finally(() => setLoaded(true));
  }, []);

  if (!loaded || brands.length === 0) return null;

  return (
    <section className="py-28 ft-section-dark" data-testid="home-turnkey">
      <div className="ft-container">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 text-[0.6875rem] uppercase tracking-[0.32em] font-semibold" style={{ color: "var(--ft-gold)" }}>
              <Sparkles size={14} strokeWidth={1.75} /> Business-in-a-Box
            </div>
            <span className="ft-gold-rule-lg mt-5 block" />
            <h2 className="mt-6 text-white text-[clamp(1.875rem,3.75vw,3rem)] font-bold leading-[1.05] tracking-tight">
              Turnkey Beverage Brands,{" "}
              <span className="ft-serif font-medium" style={{ color: "var(--ft-gold)" }}>
                ready to launch
              </span>
              .
            </h2>
            <p className="mt-5 text-white/60 leading-relaxed max-w-xl">
              Fully built private-label brands — name, domain, website, and label design included.
              Acquire yours through a consultative purchase process with FLOWTALIS.
            </p>
          </div>
          <Link
            to="/turnkey"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold border-2 border-[var(--ft-gold)] text-[var(--ft-gold)] hover:bg-[var(--ft-gold)] hover:text-[var(--ft-black)] transition-all"
            data-testid="home-turnkey-view-all"
          >
            View All Brands <ArrowRight size={14} />
          </Link>
        </div>

        <div className="grid md:grid-cols-3 gap-5" data-testid="home-turnkey-grid">
          {brands.map((b) => {
            const isSold = b.status === "sold" || b.status === "archived";
            return (
            <Link
              key={b.slug}
              to={`/turnkey/${b.slug}`}
              className="group block rounded-2xl overflow-hidden border border-white/10 bg-white/[0.03] hover:border-[var(--ft-gold)] transition-all"
              data-testid={`home-turnkey-card-${b.slug}`}
            >
              <div
                className="relative aspect-[4/5] flex items-center justify-center overflow-hidden"
                style={{
                  background:
                    "linear-gradient(135deg, #0d0d0d 0%, #1a1a1a 55%, #0a0a0a 100%)",
                }}
              >
                {b.hero_image_url ? (
                  <img
                    src={b.hero_image_url}
                    alt={`${b.brand_name} — turnkey beverage brand`}
                    className={`w-full h-full object-contain p-6 transition-transform duration-500 group-hover:scale-[1.04] ${isSold ? "grayscale-[40%]" : ""}`}
                    loading="lazy"
                    decoding="async"
                  />
                ) : (
                  <div className="text-[var(--ft-gold)] text-2xl font-bold tracking-[0.24em]">
                    {b.brand_name?.toUpperCase()}
                  </div>
                )}
                {isSold && (
                  <div className="absolute top-4 left-4 text-[0.625rem] uppercase tracking-[0.28em] font-bold px-2.5 py-1 rounded-full"
                       style={{ background: "#7a1f1f", color: "#fff" }}>
                    SOLD
                  </div>
                )}
              </div>
              <div className="p-6">
                <div className="text-[0.6rem] uppercase tracking-[0.24em] font-semibold text-[var(--ft-gold)] mb-1.5">
                  {b.default_beverage_type || b.category || "Turnkey Brand"}
                </div>
                <h3 className="text-lg font-bold tracking-tight text-white">{b.brand_name}</h3>
                <div className="mt-3 flex items-center justify-between">
                  <span className="text-xs text-white/50">{isSold ? "Sold" : (b.status_label || "Available")}</span>
                  <ArrowRight
                    size={16}
                    className="opacity-50 group-hover:opacity-100 group-hover:translate-x-1 transition-all"
                    style={{ color: "var(--ft-gold)" }}
                  />
                </div>
              </div>
            </Link>
          );})}
        </div>
      </div>
    </section>
  );
};

export default HomeTurnkeyFeatured;
